<script setup lang="ts">
const { t: $t } = useI18n()

import {
  computed,
  navigateTo,
  ref,
  useI18n,
  useRoute,
  useRuntimeConfig,
} from '#imports';
import {
  asAuthUser,
  Breadcrumbs,
  Footer,
  Header,
  Logo,
  MainComponent,
  Spinner,
  getUniqueKey,
} from 'r3ui-lib';
import { useBreadcrumbs } from 'r3ui-lib/useBreadcrumbs';
import { useAuthStore } from '~/stores/auth';

import useMain from '~/composables/main/useMain';
import LanguageSelector from '~/modules/locale/LanguageSelector.vue';
import { buildSidebarLinks } from '~/composables/layout/buildSidebarLinks';
const { t: translate } = useI18n();

const { t } = useI18n();
const authStore = useAuthStore();
const headerUser = computed(() => asAuthUser(authStore.user));
const config = useRuntimeConfig();

// Инициализируем useMain для предоставления инжекций
useMain();

// Инициализируем breadcrumbs
const { crumbs, goTo } = useBreadcrumbs();

const route = useRoute();

// Локальный спиннер лэйаута (provide убран — потребителей inject нет)
const isSpinnerVisible = ref(false);

const sidebarLinks = computed(() =>
  buildSidebarLinks({
    path: route.path,
    t,
    hasPermission: permission => authStore.hasUserPermission(permission),
    authLink: config.public.authLink as string | undefined,
    handbookLink: config.public.handbookLink as string | undefined,
    createUniqueKey: getUniqueKey,
  })
);

const dropdownLinks = computed(() => {
  return [
    {
      name: `${config?.public?.authLink}/profile`,
      title: translate('auth.profile'),
    },
    {
      name: 'logout',
      title: translate('auth.Logout'),
    },
  ];
});

const loginText = computed(() => {
  if (route.name === 'login') {
    return $t('auth.Authorization');
  }
  return '';
});
</script>

<template>
  <Header
    :is-authenticated="authStore.isAuthenticated"
    :user="headerUser"
    :brand-text="$t('header.automated_animal_recording_system')"
    :login-text="loginText"
    :raw-dropdown-links="dropdownLinks"
    @navigate="navigateTo"
    @logout="() => authStore.logout()"
  >
    <template #logo>
      <Logo @click="navigateTo('/')" />
    </template>
    <template #language-selector>
      <LanguageSelector />
    </template>
  </Header>
  <MainComponent
    :is-authenticated="authStore.isAuthenticated"
    :sidebar-links="sidebarLinks"
    @navigate="navigateTo"
  >
    <template #breadcrumbs>
      <Breadcrumbs
        v-if="authStore.isAuthenticated"
        :crumbs="crumbs"
        @navigate="goTo"
      />
    </template>
    <div class="ps-2 position-relative">
      <div
        v-if="isSpinnerVisible"
        class="spinner-overlay"
      >
        <div class="spinner-container">
          <Spinner />
        </div>
      </div>
      <slot />
    </div>
  </MainComponent>
  <Footer :copyright-text="$t('auth.rights')" />
</template>

<style scoped>
.spinner-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.spinner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}
</style>
